import { useEffect, useMemo, useState } from 'react';
import { CaretDown, CheckCircle, Circle, Info, LockSimple, Percent, Sparkle, Wallet, Waves } from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useCreateIncomeSourceMutation,
  useIncomeSourcesQuery,
  useUpdateIncomeSourceMutation,
} from '../../features/income/queries';
import { RECURRENCE_BUCKETS } from '../../lib/incomeSourceBuckets';
import pt from '../../locales/pt';
import { pickNextIncomeSourceColor, INCOME_SOURCE_COLOR_PALETTE } from '../../utils/incomeSourceColors';
import {
  defaultIconNameForCategory,
  INCOME_CATEGORY_ICON_NAMES,
  resolveIncomeSourceIcon,
} from '../../utils/incomeSourceIcons';
import { parsePtNumber, formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import { NumericInput } from '../common/NumericInput';
import { Button } from '../ui/Button';
import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Input } from '../ui/Input';

const WIZARD_CATEGORIES = [
  'salary',
  'freelance',
  'rental',
  'capital_gains',
  'crypto',
  'extraordinary',
];

const RECURRENCE_ICONS = {
  guaranteed: LockSimple,
  variable: Waves,
  occasional: Sparkle,
};

const RECURRENCE_OPTIONS = RECURRENCE_BUCKETS.map((bucket) => ({
  recurrence: bucket.recurrence,
  bucket,
  icon: RECURRENCE_ICONS[bucket.id],
  titleKey: bucket.id === 'guaranteed' ? 'fixedTitle' : bucket.id === 'variable' ? 'variableTitle' : 'occasionalTitle',
  descKey: bucket.id === 'guaranteed' ? 'fixedDesc' : bucket.id === 'variable' ? 'variableDesc' : 'occasionalDesc',
  bucketKey: bucket.id === 'guaranteed' ? 'fixedBucket' : bucket.id === 'variable' ? 'variableBucket' : 'occasionalBucket',
}));

/** `.pen` → `ds/form/field-group--default`: label + input + ajuda opcional. */
function FieldLabel({ htmlFor, optional, children }) {
  return (
    <div className="flex items-center justify-between gap-space-2">
      <label htmlFor={htmlFor} className="type-label text-foreground">
        {children}
      </label>
      {optional ? <span className="type-label text-muted-foreground">{optional}</span> : null}
    </div>
  );
}

function FieldHelp({ children }) {
  if (!children) return null;
  return <p className="type-body-sm font-normal text-muted-foreground">{children}</p>;
}

const emptyForm = () => ({
  recurrence: 'monthly_fixed',
  name: '',
  category: 'salary',
  base_amount: '',
  is_net: true,
  color: '',
  icon: '',
  notes: '',
});

/**
 * Sheet de ecrã único — criar/editar fonte. `.pen` `Fontes de Rendimento ·
 * Sheet` (mesma família do `Ativos · Sheet`): escolha de tipo no topo,
 * campos que se ajustam, pergunta líquido/bruto, aparência opcional
 * colapsada, rodapé com botão único. Compatível com `onSubmitExternal`
 * (QuickIncomeSheet cria fontes inline sem sair do registo de rendimento).
 */
export function IncomeSourceWizardSheet({
  isOpen,
  onClose,
  initialValues,
  onSubmitExternal,
  isPendingExternal,
  initialBucketId,
}) {
  const isEdit = Boolean(initialValues?.id) && !onSubmitExternal;
  const { data: existingSources = [] } = useIncomeSourcesQuery({ enabled: isOpen });
  const createMutation = useCreateIncomeSourceMutation();
  const updateMutation = useUpdateIncomeSourceMutation();
  const isPending =
    isPendingExternal ?? (createMutation.isPending || updateMutation.isPending);

  const [form, setForm] = useState(emptyForm);
  const [nameError, setNameError] = useState('');
  const [showAppearance, setShowAppearance] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const recurrenceFromBucket =
      initialBucketId === 'variable'
        ? 'monthly_variable'
        : initialBucketId === 'occasional'
          ? 'occasional'
          : initialBucketId === 'guaranteed'
            ? 'monthly_fixed'
            : null;

    setNameError('');
    setShowAppearance(false);
    setForm({
      recurrence:
        initialValues?.recurrence || recurrenceFromBucket || 'monthly_fixed',
      name: initialValues?.name || '',
      category: initialValues?.category || 'salary',
      base_amount:
        initialValues?.base_amount !== undefined && initialValues?.base_amount !== null
          ? String(initialValues.base_amount).replace('.', ',')
          : '',
      is_net: initialValues?.is_net !== false,
      color: initialValues?.color || '',
      icon: initialValues?.icon || '',
      notes: initialValues?.notes || '',
    });
  }, [isOpen, initialValues, initialBucketId]);

  const amountPreview = useMemo(() => {
    if (!form.base_amount.trim()) return null;
    const n = parsePtNumber(form.base_amount);
    return n !== null ? formatEUR(n) : null;
  }, [form.base_amount]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const buildPayload = () => {
    const amount = form.base_amount.trim() ? parsePtNumber(form.base_amount) : null;
    const payload = {
      name: form.name.trim(),
      category: form.category,
      recurrence: form.recurrence,
      base_amount: amount !== null ? amount.toFixed(2) : null,
      is_net: Boolean(form.is_net),
      notes: form.notes || '',
    };
    if (form.color) payload.color = form.color;
    if (form.icon) payload.icon = form.icon;

    if (!isEdit) {
      if (!payload.color) {
        payload.color = pickNextIncomeSourceColor(existingSources);
      }
      if (!payload.icon) {
        payload.icon = defaultIconNameForCategory(form.category);
      }
    }
    return payload;
  };

  const submit = () => {
    if (!form.name.trim()) {
      setNameError('Nome obrigatório.');
      return;
    }
    const payload = buildPayload();

    if (onSubmitExternal) {
      Promise.resolve(onSubmitExternal(payload)).catch((err) => {
        const message =
          (err && (err.detail || err.name || err.base_amount)) ||
          'Não foi possível guardar a fonte.';
        toast.error(typeof message === 'string' ? message : JSON.stringify(message));
      });
      return;
    }

    const run = isEdit
      ? updateMutation.mutateAsync({ id: initialValues.id, ...payload })
      : createMutation.mutateAsync(payload);

    Promise.resolve(run)
      .then(() => {
        toast.success(
          isEdit
            ? pt.income.sources.form.submitEdit
            : pt.income.sources.form.submitCreate,
        );
        onClose();
      })
      .catch((err) => {
        const message =
          (err && (err.detail || err.name || err.base_amount)) ||
          'Não foi possível guardar a fonte.';
        toast.error(typeof message === 'string' ? message : JSON.stringify(message));
      });
  };

  return (
    <BottomSheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <BottomSheetContent
        title={isEdit ? pt.income.sources.form.titleEdit : pt.income.sources.form.titleCreate}
        footer={
          <Button type="button" className="w-full" disabled={isPending} onClick={submit}>
            {isPending
              ? pt.common.loading
              : isEdit
                ? pt.income.sources.form.submitEdit
                : pt.income.sources.form.submitCreate}
          </Button>
        }
        className="md:max-w-lg"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-space-4 overflow-y-auto px-4 py-4 sm:px-5">
          <p className="type-body-sm text-muted-foreground">{pt.income.sources.wizard.step1Help}</p>

          <div className="flex flex-col gap-space-2">
            <p className="type-label font-semibold text-foreground">{pt.income.sources.wizard.step1Title}</p>
            {RECURRENCE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = form.recurrence === opt.recurrence;
              return (
                <button
                  key={opt.recurrence}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => patch({ recurrence: opt.recurrence })}
                  className={cn(
                    'flex flex-col gap-space-2 rounded-ds-md border p-space-4 text-left transition-colors',
                    selected ? 'border-2 border-sage-500 bg-sage-tint' : 'border-border bg-card hover:bg-muted',
                  )}
                >
                  <span className="flex items-center gap-space-2">
                    <span className="type-body min-w-0 flex-1 font-semibold text-foreground">
                      {pt.income.sources.wizard[opt.titleKey]}
                    </span>
                    {selected ? (
                      <CheckCircle className="h-4 w-4 shrink-0 text-sage-on-tint" aria-hidden />
                    ) : (
                      <Circle className="h-4 w-4 shrink-0 text-input-border" aria-hidden />
                    )}
                  </span>
                  <p className="type-body-sm font-normal text-muted-foreground">
                    {pt.income.sources.wizard[opt.descKey]}
                  </p>
                  <span
                    className={cn(
                      'inline-flex w-fit items-center gap-1 rounded-ds-full px-space-2 py-0.5 type-chip font-semibold',
                      opt.bucket.tone.chipBg,
                      opt.bucket.tone.chipFg,
                    )}
                  >
                    <Icon className="h-3 w-3" aria-hidden />
                    {pt.income.sources.wizard[opt.bucketKey]}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-space-2">
            <FieldLabel htmlFor="income-source-name">{pt.income.sources.form.nameLabel}</FieldLabel>
            <Input
              id="income-source-name"
              value={form.name}
              onChange={(e) => {
                patch({ name: e.target.value });
                if (nameError) setNameError('');
              }}
              placeholder={pt.income.sources.form.namePlaceholder}
            />
            {nameError ? (
              <p className="type-body-sm text-error">{nameError}</p>
            ) : (
              <FieldHelp>{pt.income.sources.form.nameHelp}</FieldHelp>
            )}
          </div>

          <div className="flex flex-col gap-space-2">
            <FieldLabel>{pt.income.sources.form.categoryLabel}</FieldLabel>
            <div className="grid grid-cols-3 gap-space-2">
              {WIZARD_CATEGORIES.map((cat) => {
                const iconName = INCOME_CATEGORY_ICON_NAMES[cat];
                const Icon = resolveIncomeSourceIcon({ category: cat, icon: iconName });
                const selected = form.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => patch({ category: cat, icon: defaultIconNameForCategory(cat) })}
                    className={cn(
                      'flex flex-col items-center gap-1.5 rounded-ds-md border-2 py-space-2-5 text-center type-chip font-semibold transition-colors',
                      selected
                        ? 'border-sage-500 bg-sage-tint text-sage-on-tint'
                        : 'border border-input-border bg-card font-normal text-card-foreground hover:border-stone-400 dark:hover:border-stone-500',
                    )}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                    <span>{pt.income.sources.categories[cat]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-space-2">
            <FieldLabel htmlFor="income-source-amount" optional={pt.income.sources.wizard.optional}>
              {pt.income.sources.form.baseAmountLabel}
            </FieldLabel>
            <NumericInput
              id="income-source-amount"
              value={form.base_amount}
              onChange={(e) => patch({ base_amount: e.target.value })}
            />
            <FieldHelp>{pt.income.sources.form.baseAmountHelp}</FieldHelp>
          </div>

          <div className="flex flex-col gap-space-2">
            <p className="type-label font-semibold text-foreground">
              {amountPreview ? (
                <>
                  Os {amountPreview} {pt.income.sources.wizard.netQuestion}
                </>
              ) : (
                pt.income.sources.wizard.netQuestionEmpty
              )}
            </p>
            <div className="grid grid-cols-1 gap-space-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => patch({ is_net: true })}
                className={cn(
                  'flex flex-col gap-1 rounded-ds-md border p-space-2-5 text-left',
                  form.is_net ? 'border-2 border-sage-500 bg-sage-tint' : 'border-border bg-card',
                )}
              >
                <span className="flex items-center gap-1.5">
                  <Wallet className={cn('h-4 w-4', form.is_net ? 'text-sage-on-tint' : 'text-muted-foreground')} aria-hidden />
                  <span className={cn('type-body-sm font-semibold', form.is_net ? 'text-sage-on-tint' : 'text-card-foreground')}>
                    {pt.income.sources.wizard.netYesTitle}
                  </span>
                </span>
                <span className="type-chip font-normal text-muted-foreground">{pt.income.sources.wizard.netYesDesc}</span>
              </button>
              <button
                type="button"
                onClick={() => patch({ is_net: false })}
                className={cn(
                  'flex flex-col gap-1 rounded-ds-md border p-space-2-5 text-left',
                  !form.is_net ? 'border-2 border-sage-500 bg-sage-tint' : 'border-border bg-card',
                )}
              >
                <span className="flex items-center gap-1.5">
                  <Percent className={cn('h-4 w-4', !form.is_net ? 'text-sage-on-tint' : 'text-muted-foreground')} aria-hidden />
                  <span className={cn('type-body-sm font-semibold', !form.is_net ? 'text-sage-on-tint' : 'text-card-foreground')}>
                    {pt.income.sources.wizard.netNoTitle}
                  </span>
                </span>
                <span className="type-chip font-normal text-muted-foreground">{pt.income.sources.wizard.netNoDesc}</span>
              </button>
            </div>
            <div className="flex items-start gap-space-3 rounded-ds-lg border border-info-500 bg-info-tint p-space-3">
              <Info className="h-4 w-4 shrink-0 text-info-fg" aria-hidden />
              <span className="type-body-sm font-normal text-muted-foreground">
                {pt.income.sources.wizard.netNote}
              </span>
            </div>
          </div>

          <div className="rounded-ds-lg border border-border bg-card">
            <button
              type="button"
              className="flex w-full items-center gap-space-3 px-space-4 py-space-3 text-left"
              onClick={() => setShowAppearance((v) => !v)}
            >
              <span className="flex-1">
                <span className="block type-body font-semibold text-card-foreground">
                  {pt.income.sources.wizard.colorIconOptional}
                </span>
                <span className="block type-body-sm text-muted-foreground">
                  {pt.income.sources.wizard.colorIconHint}
                </span>
              </span>
              <CaretDown
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                  showAppearance && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
            {showAppearance ? (
              <div className="flex flex-col gap-space-3 border-t border-border px-space-4 pb-space-4 pt-space-3">
                <div>
                  <p className="mb-space-2 type-label text-muted-foreground">
                    {pt.income.sources.form.colorLabel}
                  </p>
                  <div className="flex flex-wrap gap-space-2">
                    {INCOME_SOURCE_COLOR_PALETTE.map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-label={color}
                        onClick={() => patch({ color })}
                        className={cn(
                          'h-8 w-8 rounded-full border-2',
                          form.color === color ? 'border-foreground' : 'border-transparent',
                        )}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}

/** @deprecated Use IncomeSourceWizardSheet — kept for import compatibility. */
export function IncomeSourceFormDialog(props) {
  return <IncomeSourceWizardSheet {...props} />;
}

export default IncomeSourceWizardSheet;
